import { createServerFn } from "@tanstack/react-start";
import { authMiddleware } from "#/modules/util.auth/middleware.auth";
import { getRequest, setResponseHeader } from "@tanstack/react-start/server";
import { Result } from "better-result";
import { getBeweegactiviteitenreeksen } from "#/modules/beweegactiviteitenreeks.overview/api.beweegactiviteitenreeksen-overview.server";
import type {
  ActivitiesSearch,
  AdminActivitiesSearch,
} from "#/modules/beweegactiviteitenreeks.overview/schema.search";
import {
  createBeweegActiviteit,
  deleteBeweegActiviteit,
  getBeweegactiviteitenreeksDetail,
  updateBeweegactiviteitenreeksPublicatie,
  updateBeweegActiviteit,
} from "#/modules/beweegactiviteitenreeks/api.beweegactiviteiten.server";
import { getAdminBeweegactiviteitenreeksen } from "#/modules/beweegactiviteitenreeks/api.beweegactiviteiten-admin.server";
import {
  BEWEEGACTIVITEIT_COOKIE_NAME,
  getBeweegactiviteitenreeksDetailCookieFromRequest,
} from "#/modules/beweegactiviteitenreeks/cookie.beweegactiviteiten";
import { apiFailure } from "#/modules/util.errors/api-failure.server";
import { toFieldErrors, translateApiError } from "#/modules/util.errors/translate";
import { toastErrorMiddleware } from "#/modules/util.errors/middleware.toast-errors";
import { m } from "#/paraglide/messages";
import type { components } from "#/schema";

export const getSchoolActivitiesOverview = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .validator((data: ActivitiesSearch) => data)
  .handler(async ({ data }) => {
    const result = await getBeweegactiviteitenreeksen({ ...data });
    if (Result.isError(result)) throw result.error;
    const response = result.value;

    const totalPages = Math.ceil((response.count || 0) / data.pageSize);

    return {
      beweegactiviteitenreeksen: response.data || [],
      totalPages,
      totalCount: response.count || 0,
    };
  });

export const getAdminActivitiesOverview = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .validator((data: AdminActivitiesSearch) => data)
  .handler(async ({ data }) => {
    const result = await getAdminBeweegactiviteitenreeksen(data);
    if (Result.isError(result)) throw result.error;
    const response = result.value;
    const totalPages = Math.ceil((response.count || 0) / data.pageSize);

    return {
      activities: response.data || [],
      totalPages,
      totalCount: response.count || 0,
    };
  });

export const getActivityAddPage = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .handler(async () => {
    const request = getRequest();
    return {
      beweegactiviteitenreeks: getBeweegactiviteitenreeksDetailCookieFromRequest(request),
      isEditRoute: false,
    };
  });

export const getActivityEditPage = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .validator((data: { id: string }) => data)
  .handler(async ({ data }) => {
    const result = await getBeweegactiviteitenreeksDetail(data.id);

    if (Result.isError(result)) {
      throw result.error;
    }

    return {
      beweegactiviteitenreeks: result.value.beweegactiviteitenreeks,
      isEditRoute: true,
    };
  });

export const getActivityDetail = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .validator((data: { id: string }) => data)
  .handler(async ({ data }) => {
    const result = await getBeweegactiviteitenreeksDetail(data.id);

    if (Result.isError(result)) {
      throw result.error;
    }

    return {
      beweegactiviteitenreeks: result.value.beweegactiviteitenreeks,
    };
  });

export const submitCreateActivity = createServerFn({ method: "POST" })
  .middleware([authMiddleware])
  .validator((data: { beweegactiviteitenreeks: unknown }) => data)
  .handler(async ({ data }) => {
    // SAFETY: the form posts its reeks detail model as-is; the backend validates every field.
    const beweegactiviteitenreeks =
      data.beweegactiviteitenreeks as components["schemas"]["BeweegactiviteitenreeksDetailModel"];

    const result = await createBeweegActiviteit(beweegactiviteitenreeks);

    if (Result.isError(result)) {
      const fieldErrors = toFieldErrors(result.error, {
        "beweegactiviteitenreeks.beweegactiviteiten": () =>
          m["beweegactiviteitenreeks.api_validation_errors.beweegactiviteiten_required"](),
      });
      return fieldErrors ? { fieldErrors } : { formError: translateApiError(result.error) };
    }

    setResponseHeader("Set-Cookie", `${BEWEEGACTIVITEIT_COOKIE_NAME}=; Max-Age=0; Path=/`);

    // Portal showed an "added" toast via remix-toast on the server redirect. GET/redirect cookie
    // writes don't persist here, so we return the message and let the caller toast then
    // navigate back to its own (school or admin) overview with a typed link.
    return {
      message: m["beweegactiviteitenreeks.toast_messages.added"]({
        name: beweegactiviteitenreeks.name,
      }),
    };
  });

export const submitUpdateActivity = createServerFn({ method: "POST" })
  .middleware([authMiddleware])
  .validator((data: { id: string; beweegactiviteitenreeks: unknown }) => data)
  .handler(async ({ data }) => {
    // SAFETY: the form posts its reeks detail model as-is; the backend validates every field.
    const beweegactiviteitenreeks =
      data.beweegactiviteitenreeks as components["schemas"]["BeweegactiviteitenreeksDetailModel"];

    const loadResult = await getBeweegactiviteitenreeksDetail(data.id);
    if (Result.isError(loadResult)) {
      throw loadResult.error;
    }

    const result = await updateBeweegActiviteit(beweegactiviteitenreeks);

    if (Result.isError(result)) {
      const fieldErrors = toFieldErrors(result.error, {
        "beweegactiviteitenreeks.beweegactiviteiten": () =>
          m["beweegactiviteitenreeks.api_validation_errors.beweegactiviteiten_required"](),
      });
      return fieldErrors ? { fieldErrors } : { formError: translateApiError(result.error) };
    }

    return {
      message: m["beweegactiviteitenreeks.toast_messages.updated"]({
        name: beweegactiviteitenreeks.name,
      }),
    };
  });

export const submitDeleteActivity = createServerFn({ method: "POST" })
  .middleware([authMiddleware])
  .validator((data: { id: string }) => data)
  .handler(async ({ data }) => {
    const loadResult = await getBeweegactiviteitenreeksDetail(data.id);
    if (Result.isError(loadResult)) {
      throw loadResult.error;
    }
    const detail = loadResult.value;

    const result = await deleteBeweegActiviteit([data.id]);

    if (Result.isError(result)) {
      return { formError: translateApiError(result.error) };
    }

    // Portal showed a "deleted" toast via remix-toast on the server redirect; the caller toasts +
    // navigates (typed) here instead.
    return {
      message: m["beweegactiviteitenreeks.toast_messages.deleted"]({
        name: detail.beweegactiviteitenreeks.name,
      }),
    };
  });

export const togglePublishActivity = createServerFn({ method: "POST" })
  .middleware([authMiddleware, toastErrorMiddleware])
  .validator((data: { reeksId: string; isPublished: boolean }) => data)
  .handler(async ({ data }) => {
    const result = await updateBeweegactiviteitenreeksPublicatie(data.reeksId, !data.isPublished);

    if (Result.isError(result)) {
      return apiFailure(result.error);
    }
  });
